import * as React from "react";
import { Button, Dropdown, notification } from "antd";
import { VersionWrapper } from "./version-wrapper";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ContainersWrapper } from "./containers-wrapper";
import { linksOf, request } from "../../services/cpln";
import { deploymentContainerSort, k8sKeySort, toSortedJSON } from "../../services/utils";
import { Activity, AlertCircle, MoreVertical } from "react-feather";
import jsYaml from "js-yaml";
import { Message } from "../../pages/workload/statefulDeployments/message";
import { Tooltip } from "../Tooltip";
import { Deployment } from "../../schema/types/workload/deployment";
import { sortDeployments } from "../../mst/kinds/workload.helpers";
import clsx from "clsx";
import { BasePathContext } from "../../reactContexts/basePathContext";
import { Workload } from "../../schema/types/workload/entity";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { ExternalLink } from "../../newcomponents/table/components/ExternalLink";
import { setDeploymentViewObject } from "../../store/workloadSlice";
import { parseLink } from "../../utils/linkParser/linkParser";

interface Props {
  workload: Workload;
  deployments: Deployment[];
  isDeploymentsLoaded: boolean;
}
export const DeploymentTable: React.FC<Props> = ({ workload, deployments, isDeploymentsLoaded }) => {
  const dispatch = useAppDispatch();

  const selfLink = linksOf(workload).self!;

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const org = useAppSelector(selectOrg);
  const basePath = React.useContext(BasePathContext);

  const [collapsedDeployments, setCollapsedDeployments] = React.useState<string[]>([]);

  function onToggleCollapseDeployment(deployment: string) {
    let _collapsedDeployments = [...collapsedDeployments];
    if (_collapsedDeployments.includes(deployment)) {
      _collapsedDeployments = _collapsedDeployments.filter((x) => x !== deployment);
    } else {
      _collapsedDeployments.push(deployment);
    }
    setCollapsedDeployments(_collapsedDeployments);
  }

  async function handleExportMenuClick(e: any) {
    if (e.key === "json") {
      await exportJSON();
    } else if (e.key === "yaml") {
      await exportYaml();
    }
  }

  async function exportJSON() {
    try {
      const a = document.createElement("a");
      a.style.display = "none";
      a.classList.add("cpln-temp-a");
      a.download = workload.name + "-deployments.json";
      let blob = "";
      const { data } = await request({ url: selfLink + "/deployment" });
      blob = JSON.stringify(toSortedJSON(data), null, 2);
      const file = new Blob([blob], { type: "text/json" });
      const href = URL.createObjectURL(file);
      a.href = href;
      a.click();
    } catch (e) {
      notification.warning({ message: "Failed", description: e.message });
    }
  }

  async function exportYaml() {
    try {
      const a = document.createElement("a");
      a.style.display = "none";
      a.classList.add("cpln-temp-a");
      a.download = workload.name + "-deployments.yaml";
      let blob = "";
      const { data } = await request({ url: selfLink + "/deployment" });
      blob = jsYaml.dump(data, { indent: 2, noRefs: true, sortKeys: k8sKeySort });
      const file = new Blob([blob], { type: "text/yaml" });
      const href = URL.createObjectURL(file);
      a.href = href;
      a.click();
    } catch (e) {
      notification.warning({ message: "Failed", description: e.message });
    }
  }

  const isCollapsedAll = deployments.length === collapsedDeployments.length;

  function onToggleCollapseAll() {
    if (isCollapsedAll) {
      setCollapsedDeployments([]);
    } else {
      setCollapsedDeployments(deployments.map((d) => d.name));
    }
  }

  if (!isDeploymentsLoaded) {
    return (
      <div className="flex flex-col items-center">
        <span>Loading deployments...</span>
      </div>
    );
  }

  if (deployments.length < 1) {
    return (
      <div className="flex flex-col items-center">
        <AlertCircle className="my-4" size={16} />
        <span>No deployments found.</span>
      </div>
    );
  }

  return (
    <div className="w-full">
      {sortDeployments(deployments).map((deployment, deploymentIndex) => {
        let isCollapsed = collapsedDeployments.includes(deployment.name);

        const isReady = deployment.status?.ready;

        return (
          <div key={deployment.name} className={`${isCollapsed ? "mb-4" : "mb-10"} w-full`}>
            <div
              className={`flex items-center gap-2 text-xl font-medium w-full ${deployment.status?.endpoint ? "" : "mb-4"}`}
            >
              <span>{deployment.name}</span>
              <button
                type={"button"}
                className={`focus`}
                onClick={() => dispatch(setDeploymentViewObject({ workloadLink: selfLink, data: deployment }))}
              >
                <Activity className={`${isReady ? "color-action" : "color-danger"}`} size={16} />
              </button>
              <button
                type={"button"}
                onClick={() => onToggleCollapseDeployment(deployment.name)}
                className="text-base"
              >
                {isCollapsed ? <DownOutlined className={`feather-icon`} /> : <UpOutlined className={`feather-icon`} />}
              </button>
              {deploymentIndex === 0 && (
                <>
                  <div className="flex-1" />
                  <Button
                    type="text"
                    onClick={onToggleCollapseAll}
                    iconPosition="end"
                    icon={isCollapsedAll ? <DownOutlined /> : <UpOutlined />}
                  >
                    {isCollapsedAll ? "Expand All" : "Collapse All"}
                  </Button>
                  <Dropdown.Button
                    className="w-auto"
                    onClick={() => dispatch(setDeploymentViewObject({ workloadLink: selfLink, data: deployments }))}
                    menu={{
                      onClick: handleExportMenuClick,
                      items: [
                        { key: "json", label: "Export JSON" },
                        { key: "yaml", label: "Export YAML" },
                      ],
                    }}
                  >
                    View Deployments
                  </Dropdown.Button>
                </>
              )}
            </div>
            {deployment.status?.endpoint ? (
              <div className={`flex items-center leading-none text-sm`}>
                <div>{deployment.status.endpoint}</div>
                <ExternalLink url={deployment.status?.endpoint} label={""} />
              </div>
            ) : null}
            {deployment.status?.message ? (
              <Message
                className="mt-2"
                messages={[deployment.status.message]}
                isHealthy={isReady}
                logsLink={pathname.replace("/deployments", "/logs") + "?execute=true"}
                eventsLink={pathname.replace("/deployments", "/events")}
              />
            ) : null}
            <VersionWrapper collapsed={collapsedDeployments.includes(deployment.name)}>
              {(deployment.status?.versions || [])
                .slice()
                .sort((a: any, b: any) => b.gvc + b.workload - (a.gvc + a.workload))
                .map((version) => (
                  <React.Fragment key={version.workload}>
                    <div className="mt-4 flex items-center">
                      <span className="font-medium">Version {version.workload}</span>
                      <button
                        type={"button"}
                        onClick={() => dispatch(setDeploymentViewObject({ workloadLink: selfLink, data: version }))}
                        className={`ml-2 focus`}
                      >
                        <Activity className={`${version.ready ? "color-action" : "color-danger"}`} size={16} />
                      </button>
                    </div>
                    <Message
                      messages={[
                        version.message || "",
                        ...(version.ready ? [] : workload.status?.resolvedImages?.errorMessages || []),
                      ]}
                      isHealthy={version.ready}
                      logsLink={pathname.replace("/deployments", "/logs") + "?execute=true"}
                      eventsLink={pathname.replace("/deployments", "/events")}
                    />
                    <ContainersWrapper>
                      {Object.values(version.containers || {})
                        .sort(deploymentContainerSort(workload))
                        .map((container, containerIndex) => {
                          const lastItem = containerIndex === Object.values(version.containers || {}).length - 1;

                          return (
                            <div
                              key={container.name}
                              className={clsx("px-4", { "border-b": !lastItem })}
                              style={{
                                paddingTop: 5,
                                paddingBottom: container.message ? "10" : "5",
                              }}
                            >
                              <div className="flex items-center w-full">
                                <div className={`w-2/12 flex items-center pr-4`}>
                                  <button
                                    type={"button"}
                                    className={`focus`}
                                    onClick={() =>
                                      dispatch(setDeploymentViewObject({ workloadLink: selfLink, data: container }))
                                    }
                                  >
                                    <Activity
                                      className={`${container.ready ? "color-action" : "color-danger"}`}
                                      size={16}
                                    />
                                  </button>
                                  <Tooltip title={container.name} open={container.name!.length >= 14}>
                                    <div className="ml-2 truncate">{container.name}</div>
                                  </Tooltip>
                                </div>
                                <div className="w-3/12 pr-2 truncate">
                                  <Tooltip title={container.image} open={container.image!.length >= 19}>
                                    {container.image!.startsWith(`/org/${org}/image/`) &&
                                    !container.image!.includes("@sha256:") ? (
                                      <Link className={`ngfocus color-link`} to={`/console${container.image}`}>
                                        {container.image}
                                      </Link>
                                    ) : (
                                      <span>{container.image}</span>
                                    )}
                                  </Tooltip>
                                </div>
                                <div
                                  className={`w-1/12 pr-2 text-sm ${container.ready ? "color-action" : "color-danger"}`}
                                >
                                  {container.ready ? "Ready" : "Not Ready"}
                                </div>
                                <div className={`w-2/12 pr-2 pl-4 flex flex-col`}>
                                  {container.resources?.cpu === undefined ? null : (
                                    <div className="text-sm">{container.resources.cpu} Millicores</div>
                                  )}
                                </div>
                                <div className={`w-2/12 pr-2 flex flex-col`}>
                                  {container.resources?.memory === undefined ? null : (
                                    <div className="text-sm">{container.resources.memory} MiB</div>
                                  )}
                                </div>
                                <div className={`w-2/12 pr-2 flex items-center text-sm`}>
                                  {container.resources?.replicasReady === undefined &&
                                  container.resources?.replicas === undefined ? null : (
                                    <span>
                                      {container.resources?.replicasReady ?? "0"}/{container.resources?.replicas ?? "0"}{" "}
                                      Ready
                                    </span>
                                  )}
                                  <div className="flex-grow" />
                                  <Dropdown
                                    trigger={["click"]}
                                    placement={"bottomRight"}
                                    menu={{
                                      items: [
                                        { key: "logs", label: "Logs" },
                                        { key: "tail", label: "Tail" },
                                        { key: "connect", label: "Connect" },
                                      ],
                                      className: "menu",
                                      onClick: ({ key }) => {
                                        const workloadGvc = parseLink(selfLink, { useInputCtx: true }).gvc;

                                        switch (key) {
                                          case "logs":
                                            const logLink = `${basePath}/logs?query=${encodeURIComponent(
                                              `{gvc="${workloadGvc}", location="${deployment.name}", workload="${workload.name}", container="${container.name}"}`,
                                            )}&execute=true&live=false`;
                                            navigate(logLink);
                                            break;
                                          case "tail":
                                            const tailLink = `${basePath}/logs?query=${encodeURIComponent(
                                              `{gvc="${workloadGvc}", location="${deployment.name}", workload="${workload.name}", container="${container.name}"}`,
                                            )}&execute=true&live=true`;
                                            navigate(tailLink);
                                            break;
                                          case "connect":
                                            const connectLink = `${basePath}/connect?location=${deployment.name}&container=${container.name}&autoConnect=true`;
                                            navigate(connectLink);
                                            break;
                                          default:
                                            break;
                                        }
                                      },
                                    }}
                                  >
                                    <button type={"button"} className={`ml-2 color-link`} style={{ width: 24 }}>
                                      <MoreVertical style={{ minWidth: 18, minHeight: 18, width: 18, height: 18 }} />
                                    </button>
                                  </Dropdown>
                                </div>
                              </div>
                              <Message
                                messages={[container.message || ""]}
                                isHealthy={container.ready}
                                logsLink={pathname.replace("/deployments", "/logs") + "?execute=true"}
                                eventsLink={pathname.replace("/deployments", "/events")}
                                className={`my-2 ml-1`}
                              />
                            </div>
                          );
                        })}
                    </ContainersWrapper>
                  </React.Fragment>
                ))}
            </VersionWrapper>
          </div>
        );
      })}
    </div>
  );
};
